import { cn } from '@/lib/utils';
import { LoadingPage } from '@/libs/components/src/lib/LoadingPage';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Icons } from '@chiroup/components/icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@chiroup/components/ui/dialog';
import React, { ChangeEvent, use, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ToastContext } from '../../contexts/toast.context';

type ModalActionButton = {
  text: string;
  onClick?: () => void | Promise<void>;
  color?: ButtonColors;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  icon?: Icons;
  visible?: boolean;
  href?: string;
  tooltip?: string;
  onSelectFile?: (e: ChangeEvent<HTMLInputElement>) => void;
};

type Props = {
  isOpen: boolean;
  children?: React.ReactNode | React.ReactNode[];
  close?: () => void;
  className?: string;
  disableClickOutsideClose?: boolean;
  isFullScreen?: boolean;
  surveyModal?: boolean;
  header?:
    | React.ReactNode
    | {
        title?: string;
        description?: string;
      };
  footer?:
    | React.ReactNode
    | {
        buttons?: (ModalActionButton | React.ReactNode)[];
        sticky?: boolean;
        leftSide?: React.ReactNode;
      };
  loading?: boolean;
  size?: 'xs' | 'sm' | 'default' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full' | 'auto';
  darkBg?: boolean;
  showCloseButton?: boolean;
};

const Modal: React.FC<Props> = ({
  isOpen,
  children,
  close,
  className = '',
  disableClickOutsideClose = false,
  isFullScreen = false,
  surveyModal = false,
  header,
  footer,
  loading = false,
  size,
  darkBg = false,
  showCloseButton = true,
}) => {
  // Determine the dialog size based on props
  const dialogSize = size || (isFullScreen ? 'full' : surveyModal ? '4xl' : 'default');

  // Only use custom positioning classes for legacy support when no size is specified
  // and the dialog should use completely custom styling
  const customClassName = className;

  const { isToast } = use(ToastContext);

  const closeModal = (open: boolean) => {
    if (!open && !isToast) {
      close?.();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body?.classList.add('overflow-y-hidden');
    }

    return () => {
      document.body?.classList.remove('overflow-y-hidden');
    };
  }, [isOpen]);

  // Check if header is an object with title/description or a ReactNode
  const isHeaderObject =
    header &&
    typeof header === 'object' &&
    !React.isValidElement(header) &&
    ('title' in header || 'description' in header);
  const headerTitle = isHeaderObject
    ? (header as { title?: string; description?: string }).title
    : undefined;
  const headerDescription = isHeaderObject
    ? (header as { title?: string; description?: string }).description
    : undefined;

  // Check if footer is an object with buttons or a ReactNode
  const isFooterObject =
    footer &&
    typeof footer === 'object' &&
    !React.isValidElement(footer) &&
    ('buttons' in footer || 'sticky' in footer || 'leftSide' in footer);
  const footerButtons = isFooterObject
    ? (footer as { buttons?: ModalActionButton[]; sticky?: boolean }).buttons
    : undefined;

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal} modal>
      <DialogContent
        size={dialogSize}
        className={cn(
          customClassName,
          dialogSize !== 'full' && 'flex flex-col max-h-[90vh]',
          darkBg ? 'bg-gray-50 dark:bg-dark-gray-700' : 'dark:bg-dark-gray-900'
        )}
        onPointerDownOutside={e => {
          if (disableClickOutsideClose || isToast) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={e => {
          if (disableClickOutsideClose || isToast) {
            e.preventDefault();
          }
        }}
        showCloseButton={showCloseButton}
      >
        {header && isHeaderObject && (
          <DialogHeader
            className={cn(
              'flex-shrink-0 bg-white dark:bg-dark-gray-900',
              darkBg && 'border-b border-gray-300 dark:border-dark-gray-600'
            )}
          >
            <DialogTitle>{headerTitle || ''}</DialogTitle>
            {headerDescription && <DialogDescription>{headerDescription}</DialogDescription>}
          </DialogHeader>
        )}
        {header && !isHeaderObject && (
          <div
            className={cn(
              'flex-shrink-0 bg-white dark:bg-dark-gray-900',
              darkBg && 'border-b border-gray-300 dark:border-dark-gray-600'
            )}
          >
            {header as React.ReactNode}
          </div>
        )}
        {!header && (
          // radix cries if you don't have a title
          <DialogTitle />
        )}

        {footer ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className={cn('px-6 pb-6 pt-4')}>{children}</div>
            </div>

            {footer && isFooterObject && (
              <DialogFooter
                className={cn(
                  'bg-white dark:bg-dark-gray-900 flex-shrink-0 flex justify-between',
                  darkBg && 'border-t border-gray-300 dark:border-dark-gray-600'
                )}
              >
                <div className="flex items-center">{footer.leftSide}</div>
                <div className="flex items-center gap-x-2">
                  {footerButtons?.map((button, index) => {
                    if (React.isValidElement(button)) {
                      return button;
                    }

                    const theButton = button as ModalActionButton;

                    const tooltipIconClassName =
                      theButton.color === ButtonColors.plain
                        ? 'h-3 w-3 text-gray-500 inline-block ml-2 align-text-top'
                        : 'h-3 w-3 text-white inline-block ml-2 align-text-top';

                    // If href is provided, render as link
                    if (theButton.href && !theButton.disabled) {
                      const isExternal = theButton.href.startsWith('http');

                      if (isExternal) {
                        return (
                          <a
                            key={index}
                            href={theButton.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              key={index}
                              text={theButton.text}
                              onClick={theButton.onClick}
                              color={theButton.color || ButtonColors.plainWithBorder}
                              disabled={theButton.disabled}
                              loading={theButton.loading}
                              className={theButton.className}
                              type={theButton.type}
                              icon={theButton.icon}
                              visible={theButton.visible}
                              tooltip={theButton.tooltip}
                              tooltipIconClassName={tooltipIconClassName}
                              onSelectFile={theButton.onSelectFile}
                              fileUpload={!!theButton.onSelectFile}
                            />
                          </a>
                        );
                      } else {
                        return (
                          <Link key={index} to={theButton.href}>
                            <Button
                              key={index}
                              text={theButton.text}
                              onClick={theButton.onClick}
                              color={theButton.color || ButtonColors.plainWithBorder}
                              disabled={theButton.disabled}
                              loading={theButton.loading}
                              className={theButton.className}
                              type={theButton.type}
                              icon={theButton.icon}
                              visible={theButton.visible}
                              tooltip={theButton.tooltip}
                              tooltipIconClassName={tooltipIconClassName}
                              onSelectFile={theButton.onSelectFile}
                              fileUpload={!!theButton.onSelectFile}
                            />
                          </Link>
                        );
                      }
                    }

                    // Default button rendering
                    return (
                      <Button
                        key={index}
                        text={theButton.text}
                        onClick={theButton.onClick}
                        color={theButton.color || ButtonColors.plainWithBorder}
                        disabled={theButton.disabled}
                        loading={theButton.loading}
                        className={theButton.className}
                        type={theButton.type}
                        icon={theButton.icon}
                        visible={theButton.visible}
                        tooltip={theButton.tooltip}
                        tooltipIconClassName={tooltipIconClassName}
                        onSelectFile={theButton.onSelectFile}
                        fileUpload={!!theButton.onSelectFile}
                      />
                    );
                  })}
                </div>
              </DialogFooter>
            )}
            {footer && !isFooterObject && (
              <DialogFooter
                className={cn(
                  'bg-white dark:bg-dark-gray-900 flex-shrink-0',
                  darkBg && 'border-t border-gray-300 dark:border-dark-gray-600'
                )}
              >
                {footer as React.ReactNode}
              </DialogFooter>
            )}
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div
              className={cn('px-6 pb-6 pt-4 h-full', darkBg && 'bg-gray-50 dark:bg-dark-gray-900')}
            >
              {children}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
